// components/RechnungsCenter.tsx
import React, { useState } from 'react';

// --- ICONS ---
const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const ArchiveIcon = (props: any) => <IconBase {...props}><polyline points="21 8 21 21 3 21 3 8"/><rect width="22" height="5" x="1" y="3" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/></IconBase>;
const FileTextIcon = (props: any) => <IconBase {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></IconBase>;
const EditIcon = (props: any) => <IconBase {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></IconBase>;
const MailIcon = (props: any) => <IconBase {...props}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconBase>;
const TrashIcon = (props: any) => <IconBase {...props}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></IconBase>;
const DownloadIcon = (props: any) => <IconBase {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></IconBase>;
const EyeIcon = (props: any) => <IconBase {...props}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></IconBase>;
const ActivityIcon = (props: any) => <IconBase {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></IconBase>;
const ChevronIcon = ({ open, ...props }: any) => (
    <IconBase {...props}>
        {open
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />}
    </IconBase>
);

function formatInvoiceDate(inv: any): string {
    const raw = inv?.createdAt ?? inv?.serviceDate;
    if (!raw) {
        if (inv?.serviceDate) {
            const d = new Date(inv.serviceDate);
            return Number.isNaN(d.getTime()) ? '---' : d.toLocaleDateString('de-DE');
        }
        return '---';
    }
    if (typeof raw?.toDate === 'function') {
        return raw.toDate().toLocaleDateString('de-DE');
    }
    if (typeof raw === 'string') {
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? '---' : d.toLocaleDateString('de-DE');
    }
    if (typeof raw?.seconds === 'number') {
        return new Date(raw.seconds * 1000).toLocaleDateString('de-DE');
    }
    return '---';
}

function resolveDisplayGross(inv: any): number {
    if (inv?.grossAmount != null && Number(inv.grossAmount) > 0) {
        return Number(inv.grossAmount);
    }
    const net = Number(inv?.netAmount || inv?.net || 0);
    const reverseCharge =
        inv?.reverseCharge === true ||
        (typeof inv?.reverseCharge !== 'boolean' && Number(inv?.vatRate) === 0);
    if (reverseCharge) return net;
    return Math.round(net * 1.19 * 100) / 100;
}

// --- PROPS INTERFACE ---
interface RechnungsCenterProps {
    isClientInvoicesLoading: boolean;
    clientInvoices: any[];
    selectedHistoryInvoice: any;
    isCreatingClientInvoice: boolean;
    activeClientInvoicePreviewData: any;
    formatCurrency: (amount: number) => string;
    setSelectedHistoryInvoice: (inv: any) => void;
    handleEditInvoice: (inv: any) => void;
    sendClientManualInvoiceMail: (invoice: any, email?: string) => void;
    deleteClientInvoice: (id: string, e: React.MouseEvent) => void;
    downloadClientManualInvoice: (invoice: any) => void;
    ClientManualInvoicePrintTemplate: React.ComponentType<{ data: any }>;
}

const RechnungsCenter: React.FC<RechnungsCenterProps> = ({
    isClientInvoicesLoading,
    clientInvoices,
    selectedHistoryInvoice,
    isCreatingClientInvoice,
    activeClientInvoicePreviewData,
    formatCurrency,
    setSelectedHistoryInvoice,
    handleEditInvoice,
    sendClientManualInvoiceMail,
    deleteClientInvoice,
    downloadClientManualInvoice,
    ClientManualInvoicePrintTemplate
}) => {
    /** Zugeklappt by default — nur explizit aufgeklappte IDs */
    const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 w-full items-stretch flex-1">
            {/* LINKE SPALTE: HISTORIE */}
            <div className="bg-[#15171e] rounded-xl border border-slate-800 shadow-xl flex flex-col overflow-hidden w-full h-full min-h-[500px]">
                <div className="p-6 border-b border-slate-800 bg-[#0c0d11]">
                    <h2 className="text-lg font-black text-[#E6C299] uppercase tracking-widest flex items-center gap-2"><ArchiveIcon size={20} /> Rechnungs-Historie</h2>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-2">Zum Prüfen Zeile aufklappen</p>
                </div>
                <div className="flex-1 overflow-y-auto p-0">
                    {isClientInvoicesLoading ? (
                        <div className="p-10 text-center text-slate-500 animate-pulse font-bold uppercase tracking-widest">Lade Historie...</div>
                    ) : clientInvoices.length === 0 ? (
                        <div className="p-12 text-center text-slate-500 flex flex-col items-center gap-3">
                            <FileTextIcon size={32} className="opacity-20" />
                            <p className="uppercase tracking-widest font-bold text-sm">Noch keine Rechnungen geschrieben.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-800/50">
                            {clientInvoices.map((inv) => {
                                const isSelected = selectedHistoryInvoice?.id === inv.id;
                                const isExpanded = !!expandedIds[inv.id];
                                const dateStr = formatInvoiceDate(inv);
                                const gross = resolveDisplayGross(inv);
                                return (
                                    <div
                                        key={inv.id}
                                        className={`${isSelected ? 'bg-slate-800/80 border-l-4 border-l-[#E6C299]' : 'border-l-4 border-l-transparent hover:bg-slate-800/30'}`}
                                    >
                                        <button
                                            type="button"
                                            className="w-full text-left p-4 flex items-center gap-3 cursor-pointer"
                                            onClick={() => {
                                                setSelectedHistoryInvoice(inv);
                                                toggleExpand(inv.id);
                                            }}
                                        >
                                            <ChevronIcon open={isExpanded} size={16} className="text-slate-500 shrink-0" />
                                            <div className="flex-1 min-w-0 grid grid-cols-[72px_1fr_auto] gap-3 items-center">
                                                <span className="text-slate-400 text-xs whitespace-nowrap">{dateStr}</span>
                                                <span className="font-bold text-white truncate">
                                                    {inv.clientName || '---'}
                                                    {inv.isCorrection ? (
                                                        <span className="ml-2 text-[9px] uppercase tracking-widest text-amber-400 font-black">Korrektur</span>
                                                    ) : null}
                                                </span>
                                                <span className="font-mono text-[#E6C299] text-sm whitespace-nowrap">{formatCurrency(gross)}</span>
                                            </div>
                                        </button>

                                        {isExpanded && (
                                            <div className="px-4 pb-4 pl-12 space-y-3">
                                                <div className="text-xs text-slate-400 space-y-1">
                                                    <p><span className="text-slate-500 uppercase tracking-widest text-[9px] mr-2">Titel</span>{inv.clientFirma || inv.invoiceTitle || '—'}</p>
                                                    <p><span className="text-slate-500 uppercase tracking-widest text-[9px] mr-2">Nr.</span><span className="font-mono text-slate-300">{inv.invoiceNumber || '—'}</span></p>
                                                    <p><span className="text-slate-500 uppercase tracking-widest text-[9px] mr-2">Mail</span>{inv.recipientEmail || inv.clientEmail || '—'}</p>
                                                    <p>
                                                        <span className="text-slate-500 uppercase tracking-widest text-[9px] mr-2">Netto</span>
                                                        <span className="font-mono text-slate-300">{formatCurrency(Number(inv.netAmount || 0))}</span>
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleEditInvoice(inv)}
                                                        className="text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 p-1.5 rounded transition-colors"
                                                        title="Rechnung korrigieren"
                                                    >
                                                        <EditIcon size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            // Manuell eingetragene Empfänger-Mail hat Vorrang vor CRM
                                                            sendClientManualInvoiceMail(inv);
                                                        }}
                                                        className="text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 p-1.5 rounded transition-colors"
                                                        title="Per E-Mail senden"
                                                    >
                                                        <MailIcon size={16} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => deleteClientInvoice(inv.id, e)}
                                                        className="text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 p-1.5 rounded transition-colors"
                                                        title="Rechnung komplett löschen"
                                                    >
                                                        <TrashIcon size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* RECHTE SPALTE: LIVESCHAU */}
            <div className="bg-[#15171e] rounded-xl border border-slate-800 shadow-xl flex flex-col overflow-hidden relative w-full h-full min-h-[500px]">
                <div className="p-4 border-b border-slate-800 bg-[#0c0d11] flex justify-between items-center shrink-0">
                    <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><EyeIcon size={16} /> Live-Vorschau</h2>
                    {selectedHistoryInvoice && (
                        <button 
                            onClick={() => downloadClientManualInvoice(selectedHistoryInvoice)}
                            disabled={isCreatingClientInvoice}
                            className="bg-[#E6C299]/10 hover:bg-[#E6C299] text-[#E6C299] hover:text-black border border-[#E6C299]/30 transition-colors px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                        >
                            {isCreatingClientInvoice ? <ActivityIcon className="animate-spin" size={14} /> : <DownloadIcon size={14} />} Download PDF
                        </button>
                    )}
                </div>
                <div className="flex-1 bg-slate-900 overflow-hidden flex justify-center pt-8 relative w-full h-full">
                    {isCreatingClientInvoice && (
                        <div className="absolute inset-0 z-50 bg-[#0f172a]/80 backdrop-blur-sm flex flex-col items-center justify-center">
                            <DownloadIcon className="w-12 h-12 text-emerald-400 animate-bounce mb-4" />
                            <p className="text-white font-black tracking-widest uppercase">Verarbeite PDF...</p>
                        </div>
                    )}
                    {activeClientInvoicePreviewData ? (
                        <div className="origin-top scale-[0.45] md:scale-[0.7] transition-transform" style={{ width: '794px' }}>
                            <ClientManualInvoicePrintTemplate data={activeClientInvoicePreviewData} />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center text-slate-500 h-full w-full absolute inset-0">
                            <FileTextIcon size={40} className="mb-4 opacity-20" />
                            <p className="text-sm italic">Wähle eine Rechnung aus der Historie aus.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RechnungsCenter;
